import { useState } from "react";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { Reveal, SectionHeader, StatusChip } from "./primitives";

const CX = 360;
const CY = 360;
const RADIUS = 245;
const ANGLES = [-90, -18, 54, 126, 198];

const pos = (deg) => {
  const rad = (deg * Math.PI) / 180;
  return [CX + RADIUS * Math.cos(rad), CY + RADIUS * Math.sin(rad)];
};

const Ecosystem = () => {
  const config = useSiteConfig();
  const [active, setActive] = useState(null);

  return (
    <section id="ecosystem" className="relative py-24 lg:py-36 border-t border-line overflow-hidden" data-testid="ecosystem-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(24,183,160,0.05),transparent_65%)]" aria-hidden="true" />
      <div className="section-shell relative">
        <SectionHeader
          index={8}
          eyebrow="Ecosystem"
          title={<>One Asset.<br />An Entire Ecosystem.</>}
          sub="JCV sits at the center of a growing network of applications, tools and services. Future components are clearly marked."
          align="center"
          testId="ecosystem-header"
        />

        <Reveal className="mt-14 flex justify-center" testId="ecosystem-visual">
          <svg viewBox="0 0 720 720" className="w-full max-w-[560px] h-auto" role="img" aria-label="JCV ecosystem diagram">
            <circle cx={CX} cy={CY} r={RADIUS} fill="none" stroke="#1B3445" strokeWidth="1" strokeDasharray="3 8" />
            {config.ecosystem.map((node, i) => {
              const [x, y] = pos(ANGLES[i]);
              const isActive = active === i;
              return (
                <line
                  key={node.id}
                  x1={CX} y1={CY} x2={x} y2={y}
                  stroke={isActive ? "#18B7A0" : "#18B7A0"}
                  strokeOpacity={isActive ? 0.9 : 0.3}
                  strokeWidth={isActive ? 1.6 : 1}
                  strokeDasharray="6 14"
                  className="animate-dash-flow transition-[stroke-opacity] duration-300"
                />
              );
            })}
            {config.ecosystem.map((node, i) => {
              const [x, y] = pos(ANGLES[i]);
              const isActive = active === i;
              return (
                <g key={node.id} className="cursor-pointer" onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}>
                  <circle cx={x} cy={y} r={isActive ? 34 : 28} fill="#0D2233" stroke={isActive ? "#18B7A0" : "#1B3445"} strokeWidth="1.5" className="transition-all duration-300" />
                  <circle cx={x} cy={y} r="6" fill={isActive ? "#18B7A0" : "#3D6E8C"} className="animate-node-pulse" style={{ animationDelay: `${i * 0.6}s` }} />
                  <text x={x} y={y + 52} textAnchor="middle" fill="#91A5B5" fontSize="12" fontFamily="JetBrains Mono, monospace" letterSpacing="1">
                    {String(i + 1).padStart(2, "0")}
                  </text>
                </g>
              );
            })}
            <circle cx={CX} cy={CY} r="64" fill="#07131F" stroke="#D6B56A" strokeWidth="2" />
            <circle cx={CX} cy={CY} r="52" fill="none" stroke="#D6B56A" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="4 6" />
            <text x={CX} y={CY + 8} textAnchor="middle" fill="#D6B56A" fontSize="26" fontWeight="700" fontFamily="Outfit, sans-serif" letterSpacing="4">
              JCV
            </text>
          </svg>
        </Reveal>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {config.ecosystem.map((node, i) => (
            <Reveal key={node.id} delay={i * 0.07} testId={`ecosystem-card-${node.id}`}>
              <article
                className={`card-surface h-full rounded-xl p-6 cursor-default ${active === i ? "border-teal/60" : ""}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(null)}
              >
                <div className="font-mono text-xs text-teal tracking-[0.25em]">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="mt-3 font-display font-semibold text-mist text-base leading-snug">{node.name}</h3>
                <p className="mt-2 text-xs text-slate leading-relaxed">{node.body}</p>
                <div className="mt-4">
                  <StatusChip tone={node.status.includes("FUTURE PHASE") ? "gold" : "slate"} testId={`ecosystem-status-${node.id}`}>
                    {node.status}
                  </StatusChip>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Ecosystem;
